import React, { useEffect, useMemo } from 'react';
import { Users, Clock, DollarSign, TrendingUp } from 'lucide-react';
import { formatCurrency, formatDate } from '../../../utils/helpers';

const InitialDonationsReport = ({
  teams,
  players,
  expenses,
  walletTransactions,
  formatCurrency: formatCurrencyProp
}) => {
  const format = formatCurrencyProp || formatCurrency;

  // Debug logging to track data loading
  useEffect(() => {
    console.log('📊 InitialDonationsReport - Data received:', {
      teams: teams?.length || 0,
      expenses: expenses?.length || 0,
      walletTransactions: walletTransactions?.length || 0,
      teamsData: teams
    });
  }, [teams, expenses, walletTransactions]);

  // Use useMemo to calculate players only when data changes
  const allPlayers = useMemo(() => {
    // Check if players data is available
    if (!players || !Array.isArray(players)) {
      console.log('⚠️ InitialDonationsReport - No players data available');
      return [];
    }

    // Map players and find their teams
    return players.map(player => {
      // Find all teams this player is on
      const playerTeams = teams?.filter(t =>
        t.players?.some(p => (p.userId === player.id || p.id === player.id))
      ).map(t => t.name) || [];

      return {
        ...player,
        allTeams: playerTeams
      };
    });
  }, [players, teams]);

  // Calculate initial donation stats
  const { totalPlayers, paidCount, unpaidCount, totalCollected, totalReceivables, collectionRate } = useMemo(() => {
    if (!allPlayers || allPlayers.length === 0 || !expenses || !walletTransactions) {
      return {
        totalPlayers: 0,
        paidCount: 0,
        unpaidCount: 0,
        totalCollected: 0,

        totalReceivables: 0,
        collectionRate: 0
      };
    }

    let paidCnt = 0;
    let unpaidCnt = 0;
    let collected = 0;
    let receivables = 0;

    allPlayers.forEach(player => {
      const playerId = player.userId || player.id;
      const initialDonation = expenses.find(e =>
        (e.isInitialDonation || (e.isRegistrationFee && e.type !== 'Additional Donation')) &&
        (e.splitAmong?.includes(player.id) || e.splitAmong?.includes(playerId) ||
          e.playerEmail?.toLowerCase() === player.email?.toLowerCase())
      );

      if (initialDonation) {
        const playerPayment = walletTransactions.find(t =>
          t.type === 'payment' &&
          t.status === 'approved' &&
          t.amount < 0 &&
          (t.userId === player.id || t.userId === playerId ||
            t.playerEmail?.toLowerCase() === player.email?.toLowerCase())
        );

        if (playerPayment) {
          paidCnt++;
          collected += Math.abs(playerPayment.amount);
        } else {
          unpaidCnt++;
          receivables += initialDonation.amount || 250;
        }
      } else {
        unpaidCnt++;
        receivables += 250;
      }
    });

    const rate = allPlayers.length > 0 ? (paidCnt / allPlayers.length) * 100 : 0;

    return {
      totalPlayers: allPlayers.length,
      paidCount: paidCnt,
      unpaidCount: unpaidCnt,
      totalCollected: collected,
      totalReceivables: receivables,
      collectionRate: rate
    };
  }, [allPlayers, expenses, walletTransactions]);

  return (
    <div className="space-y-6">
      {/* Show loading/empty state if no players data */}
      {!players || players.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
          <Users className="w-12 h-12 mx-auto mb-3 text-yellow-600" />
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">Loading Player Data...</h3>
          <p className="text-yellow-700">
            {!players ? 'Waiting for players data to load...' : 'No players found in the club.'}
          </p>
          <p className="text-sm text-yellow-600 mt-2">
            If this persists, try refreshing the page.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-green-50 rounded-xl shadow-sm border-2 border-green-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-green-600 text-white rounded-full p-2">
                  <Users size={20} />
                </div>
                <span className="text-sm font-semibold text-green-900">Paid</span>
              </div>
              <div className="text-3xl font-bold text-green-700">{paidCount}</div>
              <div className="text-sm text-green-600 mt-1">
                {format(totalCollected)} collected
              </div>
            </div>

            <div className="bg-red-50 rounded-xl shadow-sm border-2 border-red-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-red-600 text-white rounded-full p-2">
                  <Clock size={20} />
                </div>
                <span className="text-sm font-semibold text-red-900">Unpaid</span>
              </div>
              <div className="text-3xl font-bold text-red-700">{unpaidCount}</div>
              <div className="text-sm text-red-600 mt-1">
                {format(totalReceivables)} receivables
              </div>
            </div>

            <div className="bg-blue-50 rounded-xl shadow-sm border-2 border-blue-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-blue-600 text-white rounded-full p-2">
                  <DollarSign size={20} />
                </div>
                <span className="text-sm font-semibold text-blue-900">Total Expected</span>
              </div>
              <div className="text-3xl font-bold text-blue-700">
                {format(totalPlayers * 250)}
              </div>
              <div className="text-sm text-blue-600 mt-1">
                {totalPlayers} × $250
              </div>
            </div>

            <div className="bg-purple-50 rounded-xl shadow-sm border-2 border-purple-200 p-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-purple-600 text-white rounded-full p-2">
                  <TrendingUp size={20} />
                </div>
                <span className="text-sm font-semibold text-purple-900">Collection Rate</span>
              </div>
              <div className="text-3xl font-bold text-purple-700">
                {collectionRate.toFixed(0)}%
              </div>
              <div className="text-sm text-purple-600 mt-1">
                {paidCount} of {totalPlayers}
              </div>
            </div>
          </div>

          {/* Players Table */}
          {allPlayers.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Players Yet</h3>
              <p className="text-gray-600">
                Add players to teams to track Initial Donations
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Player</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Team(s)</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Joined</th>
                      <th className="text-center py-4 px-6 font-semibold text-gray-700">Initial Don.</th>
                      <th className="text-center py-4 px-6 font-semibold text-gray-700">Status</th>
                      <th className="text-right py-4 px-6 font-semibold text-gray-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {allPlayers
                      .sort((a, b) => {
                        const aId = a.userId || a.id;
                        const bId = b.userId || b.id;
                        const aRegFee = expenses.find(e => (e.isInitialDonation || (e.isRegistrationFee && e.type !== 'Additional Donation')) && (e.splitAmong?.includes(a.id) || e.splitAmong?.includes(aId)));
                        const bRegFee = expenses.find(e => (e.isInitialDonation || (e.isRegistrationFee && e.type !== 'Additional Donation')) && (e.splitAmong?.includes(b.id) || e.splitAmong?.includes(bId)));
                        const aWallet = walletTransactions.find(t => (t.userId === a.id || t.userId === aId) && t.expenseId === aRegFee?.id && t.status === 'approved');
                        const bWallet = walletTransactions.find(t => (t.userId === b.id || t.userId === bId) && t.expenseId === bRegFee?.id && t.status === 'approved');
                        const aPaid = aWallet && aWallet.amount >= 250;
                        const bPaid = bWallet && bWallet.amount >= 250;

                        if (aPaid === bPaid) return a.name.localeCompare(b.name);
                        return aPaid ? 1 : -1;
                      })
                      .map(player => {
                        const playerId = player.userId || player.id;
                        const initialDonation = expenses.find(e =>
                          (e.isInitialDonation || (e.isRegistrationFee && e.type !== 'Additional Donation')) &&
                          (e.splitAmong?.includes(player.id) || e.splitAmong?.includes(playerId) ||
                            e.playerEmail?.toLowerCase() === player.email?.toLowerCase())
                        );

                        const playerPayment = walletTransactions.find(t =>
                          t.type === 'payment' &&
                          t.status === 'approved' &&
                          t.amount < 0 &&
                          (t.userId === player.id || t.userId === playerId ||
                            t.playerEmail?.toLowerCase() === player.email?.toLowerCase())
                        );

                        const pendingPayment = walletTransactions.find(t =>
                          t.type === 'payment' &&
                          t.status === 'pending' &&
                          t.amount < 0 &&
                          (t.userId === player.id || t.userId === playerId ||
                            t.playerEmail?.toLowerCase() === player.email?.toLowerCase())
                        );

                        // Debug logging for specific user
                        if (player.name.includes('Kashyap')) {
                          console.log('🔍 Debug Kashyap:', {
                            player: player,
                            playerId: player.id || player.userId,
                            email: player.email,
                            foundApprovedPayment: playerPayment,
                            foundPendingPayment: pendingPayment,
                            allTransactionsForUser: walletTransactions.filter(t =>
                              t.userId === player.id || t.userId === playerId ||
                              t.playerEmail?.toLowerCase() === player.email?.toLowerCase()
                            )
                          });
                        }

                        const isPaid = !!playerPayment;
                        const isPending = !!pendingPayment;
                        const hasRegFee = !!initialDonation;

                        return (
                          <tr key={playerId} className="hover:bg-gray-50 transition-colors">
                            <td className="py-4 px-6">
                              <div className="font-semibold text-gray-900">{player.name}</div>
                              <div className="text-sm text-gray-500">{player.email}</div>
                              {/* Debug info in UI for development */}
                              {player.name.includes('Kashyap') && !isPaid && (
                                <div className="text-xs text-red-500 mt-1">Debug: Payment not found</div>
                              )}
                            </td>
                            <td className="py-4 px-6">
                              <div className="flex flex-wrap gap-1">
                                {(player.allTeams || [player.teamName]).map((team, idx) => (
                                  <span key={idx} className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded font-medium">
                                    {team}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="py-4 px-6">
                              <span className="text-sm text-gray-600">
                                {player.joinedAt
                                  ? formatDate(player.joinedAt)
                                  : 'Unknown'
                                }
                              </span>
                            </td>
                            <td className="py-4 px-6 text-center">
                              {hasRegFee ? (
                                <span className="inline-flex items-center text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-semibold">
                                  ✓ Created
                                </span>
                              ) : (
                                <span className="inline-flex items-center text-xs bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full font-semibold">
                                  ⚠ Missing
                                </span>
                              )}
                            </td>
                            <td className="py-4 px-6 text-center">
                              {isPaid ? (
                                <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-semibold">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Paid
                                </span>
                              ) : isPending ? (
                                <span className="inline-flex items-center gap-1 text-xs bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full font-semibold">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  Pending Approval
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-3 py-1.5 rounded-full font-semibold">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                  </svg>
                                  Unpaid
                                </span>
                              )}
                            </td>
                            <td className="py-4 px-6 text-right">
                              <span className={`text-lg font-bold ${isPaid ? 'text-green-600' : 'text-red-600'
                                }`}>
                                {format(hasRegFee ? 250 : 0)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InitialDonationsReport;